import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Pencil, Trash2, Mail, Save, X, RotateCcw, Eye } from 'lucide-react';
import * as api from '../../utils/channelsApi';
import { getDefaultTemplate } from '../../utils/outreachApi';
import { renderEmailPreview, previewFacts, fallbackDefaultFor, splitCompletionBody, joinCompletionBody } from '../../utils/emailTemplatePreview';
import ConfirmModal from './ConfirmModal';
import { InsertVariableButton, styles } from './templateUI';

// 'custom' is deliberately absent. Nothing triggers it — there is no event or
// resource behind it — so offering it only invited authors to write copy that
// would never be sent. Rows already stored as 'custom' still list and still
// delete; see typeOptions below.
const TEMPLATE_TYPES = [
    { value: 'invitation', label: 'Invitation', sentHint: 'Offered in the Send dialog when you invite recipients.' },
    { value: 'reminder', label: 'Reminder', sentHint: 'For recipients who were invited but have not started. Nothing sends it yet — the reminder sender is not built, so this is authoring ahead of the feature.' },
    { value: 'completion', label: 'Completion', sentHint: 'Mailed to a respondent when they finish, with a copy of their answers. Save none and no receipt is sent.' },
];

const RETIRED_HINT = 'This type is retired and nothing sends it. Change the type above, or delete the template.';

const typeLabel = value => TEMPLATE_TYPES.find(type => type.value === value)?.label || value;

const EMPTY = { template_type: 'invitation', subject: '', body_text: '', body_html: '' };

/**
 * The fixed middle of a completion template. It is not a text box and not a
 * variable: the record of the answers always sits here, between the two
 * authored halves, so an author can see the shape of the message they are
 * writing without being able to move the part they do not control.
 */
function ResultsBand() {
    return (
        <div style={{
            margin: '10px 0 0', padding: '10px 12px', borderRadius: 6,
            border: '1px dashed #475569', background: 'rgba(148,163,184,0.08)',
            color: '#94A3B8', fontSize: 12, lineHeight: 1.5,
        }}>
            <strong style={{ color: '#CBD5E1' }}>Your responses</strong> — the questions asked, the answers
            given, our confidence in each, and the names of any files uploaded. Added automatically; not editable.
        </div>
    );
}

// A worked example of the generated middle, so the preview shows the real shape
// of the message rather than a marker the recipient will never see.
const SAMPLE_RESULTS = `Your responses

How satisfied were you with your visit?
  Very satisfied   (confidence: High)

Anything you would like us to improve?
  The waiting area could use more seating   (confidence: Medium)

Please upload your receipt
  Attached: receipt-2026-08.pdf`;

function TemplatePreview({ form, org, variables }) {
    const isCompletion = form.template_type === 'completion';
    const halves = splitCompletionBody(form.body_text);
    const previewForm = isCompletion ? { ...form, body_text: halves.opening } : form;
    const rendered = renderEmailPreview(previewForm, previewFacts(org, variables));
    const closing = isCompletion
        ? renderEmailPreview({ subject: '', body_text: halves.closing }, previewFacts(org, variables)).bodyText
        : '';
    return (
        <div style={styles.preview}>
            <div style={styles.previewHeader}><Eye size={13} style={{ marginRight: 6 }} />Preview — how this message arrives</div>
            <div style={styles.previewSubject}>{rendered.subject || '(no subject)'}</div>
            {rendered.bodyHTML ? (
                <>
                    <div style={styles.previewPartLabel}>HTML part</div>
                    <div style={styles.previewHtml} dangerouslySetInnerHTML={{ __html: rendered.bodyHTML }} />
                    <div style={styles.previewPartLabel}>Plain-text fallback</div>
                </>
            ) : null}
            <pre style={styles.previewText}>{rendered.bodyText || '(empty body)'}</pre>
            {isCompletion && (
                <>
                    <pre style={{ ...styles.previewText, opacity: 0.85 }}>{SAMPLE_RESULTS}</pre>
                    {closing ? <pre style={styles.previewText}>{closing}</pre> : null}
                </>
            )}
            {!org?.logo_url && form.body_text?.includes('{{org_logo}}') && (
                <div style={styles.previewNote}>No logo uploaded yet — add one under Organization Profile and it will appear here and in the sent mail.</div>
            )}
        </div>
    );
}

export function AccountEmailTemplatesSection({ orgId, org, canPersist = true, renderToggle, onNotice }) {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [confirmModal, setConfirmModal] = useState(null);
    const [platformDefaults, setPlatformDefaults] = useState(() => ({
        invitation: fallbackDefaultFor('invitation'),
        reminder: fallbackDefaultFor('reminder'),
        completion: fallbackDefaultFor('completion'),
    }));
    const subjectRef = useRef(null);
    const bodyRef = useRef(null);
    const openingRef = useRef(null);
    const closingRef = useRef(null);

    const load = useCallback(async () => {
        if (!orgId) return;
        setLoading(true);
        try {
            setTemplates(await api.listEmailTemplates(orgId));
        } catch (loadError) {
            setTemplates([]);
            // Localhost has no Console cookie. Keep the real refusal at the
            // transport boundary, but do not turn it into production UI copy.
            if (loadError.code !== api.NOT_SIGNED_IN) setError(loadError.message);
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    useEffect(() => { load(); }, [load]);
    // One fetch per type. Previously there was a single default — the invitation
    // — and it was used for every type, which is why choosing Completion left
    // invitation copy sitting in the fields.
    useEffect(() => {
        let alive = true;
        Promise.all(TEMPLATE_TYPES.map(type =>
            getDefaultTemplate(type.value)
                .then(result => (result?.body_text ? [type.value, { ...fallbackDefaultFor(type.value), ...result }] : null))
                .catch(() => null),
        )).then(entries => {
            const fetched = entries.filter(Boolean);
            if (!alive || fetched.length === 0) return;
            setPlatformDefaults(current => ({ ...current, ...Object.fromEntries(fetched) }));
        });
        return () => { alive = false; };
    }, []);

    const handleNew = () => {
        setEditing('new');
        const seed = platformDefaults.invitation;
        setForm({ ...EMPTY, subject: seed.subject, body_text: seed.body_text });
        setError('');
    };

    // Changing the type re-seeds subject and body, which is the whole point of
    // the control. It does that only when the author has not written anything
    // of their own: text that differs from the outgoing type's default is work
    // in progress, and switching type must never destroy it. When the text is
    // kept, the reset button below offers the new type's default explicitly.
    const handleTypeChange = (nextType) => {
        setForm(current => {
            const outgoing = platformDefaults[current.template_type];
            const incoming = platformDefaults[nextType];
            if (!incoming) return { ...current, template_type: nextType };
            const blank = !current.subject.trim() && !current.body_text.trim();
            const pristine = outgoing
                && current.subject === outgoing.subject
                && current.body_text === outgoing.body_text;
            if (!blank && !pristine) return { ...current, template_type: nextType };
            return { ...current, template_type: nextType, subject: incoming.subject, body_text: incoming.body_text };
        });
    };

    const handleEdit = (template) => {
        setEditing(template.template_id);
        setForm({
            template_type: template.template_type || 'invitation',
            subject: template.subject || '',
            body_text: template.body_text || '',
            body_html: template.body_html || '',
        });
        setError('');
    };

    const handleDelete = (id) => {
        setConfirmModal({
            title: 'Delete template?',
            message: 'Delete this template?',
            confirmLabel: 'Delete',
            danger: true,
            onConfirm: async () => {
                setConfirmModal(null);
                try {
                    await api.deleteEmailTemplate(id);
                    await load();
                    onNotice?.('Template deleted');
                } catch (deleteError) {
                    setError(deleteError.message);
                }
            },
        });
    };

    const handleSave = async () => {
        if (!form.subject.trim()) { setError('Subject is required'); return; }
        if (!form.body_text.trim()) { setError('Body is required'); return; }
        setSaving(true);
        setError('');
        try {
            const payload = {
                template_type: form.template_type,
                subject: form.subject,
                body_text: form.body_text,
                ...(form.body_html ? { body_html: form.body_html } : {}),
            };
            if (editing === 'new') await api.createEmailTemplate({ ...payload, org_id: orgId, language: form.language || 'en' });
            else await api.updateEmailTemplate(editing, payload);
            setEditing(null);
            await load();
            onNotice?.('Template saved');
        } catch (saveError) {
            setError(saveError.message);
        } finally {
            setSaving(false);
        }
    };

    // A stored row whose type is no longer offered (a legacy 'custom') must
    // still be editable and deletable, so its own value joins the options for
    // that row alone rather than leaving the select on an invalid value.
    const known = TEMPLATE_TYPES.some(type => type.value === form.template_type);
    const typeOptions = known
        ? TEMPLATE_TYPES
        : [...TEMPLATE_TYPES, { value: form.template_type, label: `${form.template_type} (retired)` }];
    const activeDefault = platformDefaults[form.template_type] || platformDefaults.invitation;
    const typeHint = TEMPLATE_TYPES.find(type => type.value === form.template_type)?.sentHint || RETIRED_HINT;

    // A completion template is two authored halves with the generated record of
    // the answers locked between them. body_text stays the single source of
    // truth; the two boxes are a view of it, so loading and saving are unchanged.
    const isCompletion = form.template_type === 'completion';
    const halves = splitCompletionBody(form.body_text);
    const setHalves = (opening, closing) =>
        setForm(current => ({ ...current, body_text: joinCompletionBody(opening, closing) }));

    return (
        <div>
            <div style={styles.commandRow}>
                {renderToggle ? renderToggle(Boolean(editing)) : <span />}
                {!editing && <button type="button" onClick={handleNew} style={styles.addBtn}><Plus size={14} style={{ marginRight: 6 }} /> New Template</button>}
            </div>

            {error && <div style={styles.error}>{error}</div>}

            {!editing ? (
                <>
                    {loading ? (
                        <p style={{ color: '#94A3B8', fontSize: 14 }}>Loading…</p>
                    ) : templates.length === 0 ? (
                        <div style={styles.empty}>
                            <Mail size={32} style={{ color: '#475569', marginBottom: 12 }} />
                            <p style={{ color: '#94A3B8', fontSize: 14, margin: 0 }}>No templates yet</p>
                            <p style={{ color: '#64748B', fontSize: 12, marginTop: 4 }}>Create an email template to use when sending survey invitations.</p>
                        </div>
                    ) : (
                        <div style={styles.list}>
                            {templates.map(template => (
                                <div key={template.template_id} style={styles.card}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                            <span style={styles.typeBadge}>{template.template_type}</span>
                                            <strong style={{ color: '#F1F5F9', fontSize: 14 }}>{template.subject || '(no subject)'}</strong>
                                        </div>
                                        <p style={{ color: '#64748B', fontSize: 12, margin: 0, lineHeight: 1.4, maxHeight: 38, overflow: 'hidden' }}>{template.body_text?.substring(0, 120) || '(empty body)'}</p>
                                    </div>
                                    <div style={styles.cardActions}>
                                        <button type="button" onClick={() => handleEdit(template)} style={styles.iconBtn} title="Edit"><Pencil size={14} /></button>
                                        <button type="button" disabled={!canPersist} onClick={() => handleDelete(template.template_id)} style={{ ...styles.iconBtn, color: '#F87171', ...(!canPersist ? styles.btnDisabled : {}) }} title={canPersist ? 'Delete' : 'A signed-in Console session is required to delete a stored template.'}><Trash2 size={14} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            ) : (
                <div style={styles.formCard}>
                    <h3 style={styles.formTitle}>{editing === 'new' ? 'New Template' : 'Edit Template'}</h3>
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>Type</label>
                        <select value={form.template_type} onChange={event => handleTypeChange(event.target.value)} style={{ ...styles.input, maxWidth: 200 }}>
                            {typeOptions.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                        </select>
                        <span style={styles.hint}>{typeHint}</span>
                    </div>

                    <div style={styles.fieldGroup}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                            <label style={{ ...styles.label, marginBottom: 0 }}>Subject</label>
                            <InsertVariableButton targetRef={subjectRef} formField="subject" setForm={setForm} variables={activeDefault.variables} />
                        </div>
                        <input ref={subjectRef} value={form.subject} onChange={event => setForm(current => ({ ...current, subject: event.target.value }))} placeholder="e.g. You're invited to {{survey_name}}" style={styles.input} />
                    </div>

                    <div style={styles.fieldGroup}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                            <label style={{ ...styles.label, marginBottom: 0 }}>{isCompletion ? 'Opening' : 'Body (plain text)'}</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <button type="button" onClick={() => setForm(current => ({ ...current, subject: activeDefault.subject, body_text: activeDefault.body_text }))} style={styles.resetBtn} title={`Replace subject and body with the ${typeLabel(form.template_type).toLowerCase()} message SamSurveyor sends by default`}><RotateCcw size={12} style={{ marginRight: 4 }} /> Use {typeLabel(form.template_type).toLowerCase()} default</button>
                                {isCompletion
                                    ? <InsertVariableButton targetRef={openingRef} formField="body" setForm={updater => setHalves(updater({ body: halves.opening }).body, halves.closing)} variables={activeDefault.variables} />
                                    : <InsertVariableButton targetRef={bodyRef} formField="body_text" setForm={setForm} variables={activeDefault.variables} />}
                            </div>
                        </div>
                        {isCompletion ? (
                            <>
                                <textarea ref={openingRef} value={halves.opening} onChange={event => setHalves(event.target.value, halves.closing)} rows={6} placeholder={'Hello {{recipient_name}},\n\nThank you for completing {{survey_name}}.'} style={{ ...styles.input, resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }} aria-label="Opening" />
                                <ResultsBand />
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '10px 0 6px' }}>
                                    <label style={{ ...styles.label, marginBottom: 0 }}>Closing</label>
                                    <InsertVariableButton targetRef={closingRef} formField="body" setForm={updater => setHalves(halves.opening, updater({ body: halves.closing }).body)} variables={activeDefault.variables} />
                                </div>
                                <textarea ref={closingRef} value={halves.closing} onChange={event => setHalves(halves.opening, event.target.value)} rows={4} placeholder={'If anything here looks wrong, please get in touch.'} style={{ ...styles.input, resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }} aria-label="Closing" />
                            </>
                        ) : (
                            <textarea ref={bodyRef} value={form.body_text} onChange={event => setForm(current => ({ ...current, body_text: event.target.value }))} rows={8} placeholder={'Dear {{recipient_name}},\n\nPlease take our survey: {{survey_link}}'} style={{ ...styles.input, resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }} />
                        )}
                        <span style={styles.hint}>Drop in {'{{org_logo}}'} to show your logo — the message is then sent as HTML with a plain-text fallback.</span>
                    </div>

                    <TemplatePreview form={form} org={org} variables={activeDefault.variables} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button type="button" onClick={handleSave} disabled={saving || !canPersist} title={!canPersist ? 'A signed-in Console session is required to save this draft.' : undefined} style={{ ...styles.saveBtn, ...(!canPersist ? styles.btnDisabled : {}) }}><Save size={14} style={{ marginRight: 6 }} /> {saving ? 'Saving…' : 'Save'}</button>
                        <button type="button" onClick={() => setEditing(null)} style={styles.cancelBtn}><X size={14} style={{ marginRight: 6 }} /> Cancel</button>
                    </div>
                </div>
            )}

            <ConfirmModal
                open={Boolean(confirmModal)}
                title={confirmModal?.title}
                message={confirmModal?.message}
                confirmLabel={confirmModal?.confirmLabel}
                danger={confirmModal?.danger}
                onConfirm={confirmModal?.onConfirm}
                onCancel={() => setConfirmModal(null)}
            />
        </div>
    );
}
